import { faStream, faTrash } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from "classnames"
import React, { useState } from "react"
import { useDispatch } from "react-redux"
import { Button, DropdownItem, DropdownMenu, DropdownToggle, Modal, ModalBody, ModalFooter, ModalHeader, UncontrolledDropdown } from "reactstrap"
import { LabeledCheckboxInput } from "web/components/generic/LabeledCheckbox"
import { LabeledTextInput } from "web/components/generic/LabeledTextInput"
import { RadioGroup } from "web/components/generic/RadioGroup"
import { WithTooltip } from 'web/components/generic/WithTooltip'
import { FightEvent, FightEventFlag, FightSegment } from "web/data/models"
import { useCatchError } from 'web/hooks/useCatchError'
import { useFocusOnMount } from 'web/hooks/useFocusOnMount'
import { useKeyShortcut } from 'web/hooks/useKeyShortcut'
import { useValidatedState } from "web/hooks/useValidatedState"
import { useEditFight_BadName } from 'web/redux/slices/edit-fight-slice'
import { setFocusToRef } from 'web/utils/jsx-utils'
import { secondsToTimestamp, strictParseTimestamp, timestampToSeconds, validTimestampOrDefault } from "web/utils/time-utils"
import { assertDefined, joinLines } from 'web/utils/utils'
import { ModalContainerProps } from "./_ModalContainer"

export type EditEventModalProps = {
    segment: FightSegment
    event: FightEvent

    setupEditMode?: {
        setupFightEvent: boolean
        onDeleteEvent: () => void
        onConvertToOutlineEvent: () => void
    }
}

export const EditEventModal: React.FC<EditEventModalProps & ModalContainerProps> = (props) => {

    const { segment, event, setupEditMode } = props

    const catchError = useCatchError()

    const disp = useDispatch()
    const timestampInputRef = useFocusOnMount()
    const [name, setName] = useState(props.event.name)
    const [flags, setFlags] = useState(props.event.flags)
    const [comment, setComment] = useState(props.event.comment ?? "")
    const [bulkEdit, setBulkEdit] = useState(false)

    const [editFightSelectors, editFightActions] = useEditFight_BadName()

    const [isRelativeToFightStart, setIsRelativeToFightStart] = useState(true)

    const childSegmentOffset = editFightSelectors.getChildSegmentOffset(props.segment)
    const maxEventOffset: number | undefined = (childSegmentOffset != null)
        ? (childSegmentOffset - props.segment.offset)
        : undefined

    const timestampValue = useValidatedState({
        value: secondsToTimestamp(event.offset),

        validator: (value) => eventTimestampValidator({
            value,
            subtractSegment: isRelativeToFightStart,
            segmentOffsetSeconds: props.segment.offset,
            maxEventOffset,
            childSegmentOffset
        }),

        onBlur: (value) => validTimestampOrDefault(value, secondsToTimestamp(
            isRelativeToFightStart
                ? event.offset
                : segment.offset - event.offset
        )),
    })

    const handleDelete = catchError(() => {
        props.setupEditMode?.onDeleteEvent()
        props.closeSelf()
    })

    const handleConvertToOutlineEvent = catchError(() => {
        props.setupEditMode?.onConvertToOutlineEvent()
        props.closeSelf()
    })

    const handleSave = catchError(() => {
        if (!timestampValue.validate()) {
            setFocusToRef(timestampInputRef)
            return
        }

        const offsetSeconds = resolveRelativeOffset(
            timestampValue.value,
            isRelativeToFightStart,
            props.segment.offset
        )

        disp(editFightActions.updateEvent({
            event: {
                eventId: props.event.id,
                name,
                offset: segment.offset + offsetSeconds,
                comment,
                flags,
            },
            opt: { bulkEdit }
        }))

        props.closeSelf()
    })

    useKeyShortcut({
        key: "s",
        modifier: "ctrl",
        callback: handleSave,
    })

    return (
        <Modal
            isOpen
            centered
            fade={false}
            toggle={props.closeSelf}
            backdrop={"static"}
        >
            <ModalHeader>
                {"Edit event"}
            </ModalHeader>

            <ModalBody className="p-3 m-0 bg-light">

                <div className={classNames(
                    "d-flex flex-column bg-light",
                )}>
                    {(setupEditMode && setupEditMode.setupFightEvent === false) && (
                        <div className={"alert-warning small p-2 border mb-3"}>
                            {'Warning — Editing this event will modify it for ALL setups tied to this outline.'}
                        </div>
                    )}

                    <div className="d-flex space-mx-3 align-items-end">
                        <LabeledTextInput
                            small
                            innerRef={timestampInputRef}
                            label="Timestamp"
                            placeholder="Ex: 01:30"
                            style={{ maxWidth: "120px" }}
                            invalid={timestampValue.validationError != null}
                            value={timestampValue.value}
                            onChange={(e) => timestampValue.setValue(e.target.value)}
                            onBlur={timestampValue.onBlur}
                        />

                        <LabeledTextInput
                            small
                            label="Name (optional)"
                            placeholder="Ex: Frenzy #5"
                            className="flex-grow-1"
                            value={name}
                            onChange={e => setName(e.target.value)}
                        />
                    </div>

                    {timestampValue.validationError != null &&
                        <div className="mt-2 small text-danger">
                            {timestampValue.validationError}
                        </div>
                    }

                    {props.segment.userCreated &&
                        <RadioGroup
                            className="mt-3"
                            selectedValue={(isRelativeToFightStart ? "fight" : "segment")}
                            items={[
                                {
                                    label: "Timestamp is relative to fight start (00:00)",
                                    value: "fight",
                                    onSelected: () => {
                                        timestampValue.clearErrors()
                                        setIsRelativeToFightStart(true)

                                        const offsetSeconds = timestampToSeconds(timestampValue.value)
                                        timestampValue.setValue(secondsToTimestamp(segment.offset + offsetSeconds))

                                    }
                                },
                                {
                                    label: `Timestamp is relative to segment start (${secondsToTimestamp(props.segment.offset)})`,
                                    value: "segment",
                                    onSelected: () => {
                                        timestampValue.clearErrors()
                                        setIsRelativeToFightStart(false)

                                        const offsetSeconds = timestampToSeconds(timestampValue.value)
                                        timestampValue.setValue(secondsToTimestamp(offsetSeconds - segment.offset))
                                    }
                                }
                            ]}
                        />
                    }

                    <hr className="w-100" />

                    <label className="small">{"Flags"}</label>
                    <div className="d-flex justify-content-start space-mx-3 p-3 bg-white border flex-wrap row-gap-2">
                        <LabeledCheckboxInput
                            label="Emphasized"
                            small
                            checked={flags.some(flag => flag === FightEventFlag.Emphasize)}
                            onChange={checked => {
                                if (checked) {
                                    setFlags(prev => [
                                        ...prev,
                                        FightEventFlag.Emphasize,
                                    ])
                                } else {
                                    setFlags(prev => [
                                        ...prev.filter(flag => flag !== FightEventFlag.Emphasize),
                                    ])
                                }
                            }}
                        />

                        <LabeledCheckboxInput
                            label="Muted"
                            small
                            checked={flags.some(flag => flag === FightEventFlag.Muted)}
                            onChange={checked => {
                                if (checked) {
                                    setFlags(prev => [
                                        ...prev,
                                        FightEventFlag.Muted,
                                    ])
                                } else {
                                    setFlags(prev => [
                                        ...prev.filter(flag => flag !== FightEventFlag.Muted),
                                    ])
                                }
                            }}
                        />

                        <LabeledCheckboxInput
                            label="Burst"
                            small
                            checked={flags.some(flag => flag === FightEventFlag.Burst)}
                            onChange={checked => {
                                if (checked) {
                                    setFlags(prev => [
                                        ...prev,
                                        FightEventFlag.Burst,
                                    ])
                                } else {
                                    setFlags(prev => [
                                        ...prev.filter(flag => flag !== FightEventFlag.Burst),
                                    ])
                                }
                            }}
                        />

                        <LabeledCheckboxInput
                            label="Movement"
                            small
                            checked={flags.some(flag => flag === FightEventFlag.Movement)}
                            onChange={checked => {
                                if (checked) {
                                    setFlags(prev => [
                                        ...prev,
                                        FightEventFlag.Movement,
                                    ])
                                } else {
                                    setFlags(prev => [
                                        ...prev.filter(flag => flag !== FightEventFlag.Movement),
                                    ])
                                }
                            }}
                        />

                        <LabeledCheckboxInput
                            label="Rot"
                            small
                            checked={flags.some(flag => flag === FightEventFlag.Rot)}
                            onChange={checked => {
                                if (checked) {
                                    setFlags(prev => [
                                        ...prev,
                                        FightEventFlag.Rot,
                                    ])
                                } else {
                                    setFlags(prev => [
                                        ...prev.filter(flag => flag !== FightEventFlag.Rot),
                                    ])
                                }
                            }}
                        />

                        <LabeledCheckboxInput
                            label="Spread"
                            small
                            checked={flags.some(flag => flag === FightEventFlag.Spread)}
                            onChange={checked => {
                                if (checked) {
                                    setFlags(prev => [
                                        ...prev,
                                        FightEventFlag.Spread,
                                    ])
                                } else {
                                    setFlags(prev => [
                                        ...prev.filter(flag => flag !== FightEventFlag.Spread),
                                    ])
                                }
                            }}
                        />
                    </div>

                    <hr className="w-100" />

                    <LabeledTextInput
                        textArea
                        small
                        label="Comment"
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        onBlur={() => setComment(comment.trim())}
                    />

                </div>

            </ModalBody>

            <ModalFooter>
                <div className={classNames(
                    'd-flex w-100 align-items-end',
                    { 'justify-content-end': setupEditMode == null || (setupEditMode != null && setupEditMode.setupFightEvent === false) },
                    { 'justify-content-between': setupEditMode != null && setupEditMode.setupFightEvent },
                )}>

                    {setupEditMode && setupEditMode.setupFightEvent && (
                        <UncontrolledDropdown className='flex-shrink-0 mr-3'>
                            <DropdownToggle
                                outline
                                size="sm"
                            >
                                <span>{"More actions"}</span>
                            </DropdownToggle>

                            <DropdownMenu>
                                <DropdownItem
                                    onClick={handleConvertToOutlineEvent}
                                    className="small space-mx-2"
                                >
                                    <FontAwesomeIcon
                                        fixedWidth
                                        icon={faStream}
                                    />
                                    <span>
                                        {'Convert to outline event'}
                                    </span>
                                </DropdownItem>
                                <DropdownItem
                                    onClick={handleDelete}
                                    className="small space-mx-2"
                                >
                                    <FontAwesomeIcon
                                        fixedWidth
                                        icon={faTrash}
                                        className={"text-danger"}
                                    />
                                    <span>
                                        {'Delete'}
                                    </span>
                                </DropdownItem>

                            </DropdownMenu>
                        </UncontrolledDropdown>
                    )}

                    <div className="d-flex justify-content-between w-100 align-items-center">
                        <WithTooltip place='top'>
                            {(ttId) => (
                                <div
                                    data-for={ttId}
                                    data-tip={joinLines([
                                        `If this is checked, all events with the same name as this event currently has`,
                                        `will be renamed to the same name that you set. No other properties will be copied.`
                                    ])}
                                >
                                    <LabeledCheckboxInput

                                        label="Bulk edit names"
                                        small
                                        checked={bulkEdit}
                                        onChange={checked => {
                                            setBulkEdit(checked)
                                        }}
                                    />
                                </div>
                            )}
                        </WithTooltip>

                        <div className="d-flex space-mx-2 align-items-end">
                            <Button
                                color="success"
                                size="sm"
                                onClick={handleSave}
                                title="Save changes (ctrl+s)"
                            >
                                {"Save changes"}
                            </Button>

                            <Button
                                outline
                                size="sm"
                                onClick={props.closeSelf}
                                title="Cancel edit (esc)"
                            >
                                {"Cancel edit"}
                            </Button>
                        </div>
                    </div>

                </div>
            </ModalFooter>
        </Modal>
    )
}

/**
 * Resolves the given time-stamp to an offset that is relative to
 * segment start
 */
function resolveRelativeOffset(
    timestamp: string,
    subtractSegment: boolean,
    segmentOffsetSeconds: number
) {
    strictParseTimestamp(timestamp)
    const eventOffset = timestampToSeconds(timestamp)

    let resolvedOffset = 0

    if (subtractSegment) {
        resolvedOffset = eventOffset - segmentOffsetSeconds
    }
    else {
        resolvedOffset = eventOffset
    }

    return resolvedOffset
}

function eventTimestampValidator(
    params: {
        value?: string
        subtractSegment: boolean
        segmentOffsetSeconds: number
        maxEventOffset?: number
        childSegmentOffset?: number
    }
) {

    // TODO: Tests around this shit
    //
    // TODO: Literally what even is going on in here
    //
    // TODO: This logic is now also partly duplicated in
    // EditFightContainer.tsx

    const {
        value,
        subtractSegment,
        segmentOffsetSeconds,
        maxEventOffset,
        childSegmentOffset
    } = params

    if (value == null || value === "") {
        return "Empty timestamp"
    }

    try {
        const timestamp = strictParseTimestamp(value)
        const relativeOffset = resolveRelativeOffset(timestamp, subtractSegment, segmentOffsetSeconds)

        if (relativeOffset < 0) {
            const minOffset = (subtractSegment)
                ? segmentOffsetSeconds
                : 0

            const minOffsetTimestamp = secondsToTimestamp(minOffset)

            return joinLines([
                `Event is too early. Has to start at or after ${minOffsetTimestamp}.`,
            ])
        }

        if (maxEventOffset != null && relativeOffset > maxEventOffset) {

            assertDefined(childSegmentOffset)

            const atOrBefore = (subtractSegment)
                ? secondsToTimestamp(childSegmentOffset)
                : secondsToTimestamp(maxEventOffset)

            return joinLines([
                `Event is too late. Has to start at or before ${atOrBefore}.`,
            ])
        }

    }
    catch (error) {
        return "Invalid timestamp format, expects MM:ss"
    }
}
